import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { updateEntity } from './actions';
import { errorMessageOf } from './api';
import { buildKaiContext } from './context';
import { initialState } from './reducer';
import * as service from './service';

let seq = 0;
const tempId = prefix => {
  seq += 1;
  return `${prefix}-${Date.now()}-${seq}`;
};

/**
 * Orchestrates the kAI drawer: conversation lifecycle, the streaming /ask call
 * and the usage meter. Streaming genuinely doesn't fit redux-saga (fetch +
 * ReadableStream), so the async flow lives here and writes to the global kai
 * store via UPDATE_ENTITY so conversation state survives drawer close and
 * navigation. All reads inside callbacks go through store.getState() to avoid
 * stale closures during a long-lived stream.
 */
export default function useKaiChat(active = false) {
  const dispatch = useDispatch();
  const store = useStore();
  const abortRef = useRef(null);
  const lastQueryRef = useRef('');
  // Monotonic "turn" id. Every action that supersedes in-flight work (new
  // question, new chat, business switch, opening a past conversation) bumps
  // this; async callbacks capture the id at start and check it before touching
  // state, so out-of-order / stale responses (and their finally blocks) can't
  // clobber a newer turn.
  const genRef = useRef(0);
  // Abort any in-flight stream AND invalidate its generation. Returns the new
  // current generation.
  const invalidate = useCallback(() => {
    abortRef.current?.abort();
    genRef.current += 1;
    return genRef.current;
  }, []);

  const kai = useSelector(s => s.kai || initialState);
  const businessList = useSelector(s => s.app?.businessList || []);
  const naSelectedBusiness = useSelector(
    s => s.nativeAnalytics?.selectedBusiness || null,
  );

  const businesses = businessList;

  const patch = useCallback(
    fields => dispatch(updateEntity(fields)),
    [dispatch],
  );

  const setMessages = useCallback(
    updater => patch({ messages: updater }),
    [patch],
  );

  // Discard a side-panel response if the business changed while it was in
  // flight (prevents a slow response for business A landing on business B).
  const stillBusiness = useCallback(
    businessId => store.getState().kai?.businessId === businessId,
    [store],
  );

  const loadUsage = useCallback(
    async businessId => {
      if (!businessId) return;
      patch({ usageLoading: true });
      try {
        const res = await service.getUsage(businessId);
        if (!stillBusiness(businessId)) return;
        patch({ usage: res.data, usageLoading: false });
      } catch {
        if (stillBusiness(businessId)) patch({ usageLoading: false });
      }
    },
    [patch, stillBusiness],
  );

  // Load every branch/brand id for the selected business into kai state so
  // /ask context can satisfy analyseit's branch_id/brand_id requirement
  // (especially for admins with empty Kong scope headers).
  const loadScope = useCallback(
    async businessId => {
      if (!businessId) return;
      patch({ scopeLoading: true, branchIds: [], brandIds: [] });
      try {
        const [branchesRes, brandsRes] = await Promise.all([
          service.listBranches(businessId),
          service.listBrands(businessId),
        ]);
        if (!stillBusiness(businessId)) return;
        const branchIds = (branchesRes.data?.results || [])
          .map(b => b?.id)
          .filter(id => id != null);
        const brandIds = (brandsRes.data?.results || [])
          .map(b => b?.id)
          .filter(id => id != null);
        patch({ branchIds, brandIds, scopeLoading: false });
      } catch {
        if (stillBusiness(businessId)) {
          patch({ scopeLoading: false, branchIds: [], brandIds: [] });
        }
      }
    },
    [patch, stillBusiness],
  );

  const loadConversations = useCallback(
    async businessId => {
      if (!businessId) return;
      patch({ conversationsLoading: true });
      try {
        const res = await service.listConversations({ businessId });
        if (!stillBusiness(businessId)) return;
        patch({
          conversations: res.data?.conversations || [],
          conversationsLoading: false,
        });
      } catch {
        if (stillBusiness(businessId))
          patch({ conversations: [], conversationsLoading: false });
      }
    },
    [patch, stillBusiness],
  );

  const setBusinessId = useCallback(
    businessId => {
      if (!businessId || businessId === store.getState().kai?.businessId)
        return;
      // Switching business starts a fresh scope: cancel + invalidate in-flight
      // work, new chat, reload side panels.
      invalidate();
      patch({
        businessId,
        conversationId: null,
        messages: [],
        errorMessage: null,
        quotaExceeded: false,
        isStreaming: false,
        activeTool: null,
        branchIds: [],
        brandIds: [],
      });
      loadConversations(businessId);
      loadUsage(businessId);
      loadScope(businessId);
    },
    [patch, store, invalidate, loadConversations, loadUsage, loadScope],
  );

  const newChat = useCallback(() => {
    invalidate();
    patch({
      conversationId: null,
      messages: [],
      errorMessage: null,
      quotaExceeded: false,
      isStreaming: false,
      activeTool: null,
    });
  }, [patch, invalidate]);

  const openConversation = useCallback(
    async conversationId => {
      const businessId = store.getState().kai?.businessId;
      const myGen = invalidate();
      patch({
        conversationId,
        messages: [],
        historyLoading: true,
        errorMessage: null,
        quotaExceeded: false,
        isStreaming: false,
        activeTool: null,
      });
      try {
        const res = await service.getConversationMessages({
          id: conversationId,
          businessId,
        });
        // Discard if a newer turn superseded this load (out-of-order response).
        if (genRef.current !== myGen) return;
        const messages = (res.data?.messages || [])
          .filter(m => m.role === 'user' || m.role === 'assistant')
          .map(m => ({
            id: m.id,
            role: m.role,
            content: m.content || '',
          }));
        patch({ messages, historyLoading: false });
      } catch {
        if (genRef.current === myGen) patch({ historyLoading: false });
      }
    },
    [patch, invalidate],
  );

  // Core streaming runner. `appendUser` is false on retry (the user turn is
  // already on screen — we only replace the failed assistant turn).
  const runAsk = useCallback(
    async (query, { appendUser } = { appendUser: true }) => {
      const state = store.getState();
      const businessId = state.kai?.businessId;
      if (!businessId) {
        patch({
          errorMessage: 'Pick a business before asking kAI.',
        });
        return;
      }
      if (state.kai?.isStreaming) return;

      // Claim this turn BEFORE any await: install the abort controller (so an
      // abort during the createConversation call actually cancels the chain)
      // and capture the generation. isCurrent() is generation-based only —
      // a user "Stop" (cancel) aborts the signal but keeps this turn current so
      // we can still finalise it; only newChat/switch/openConversation/a newer
      // question bump the generation and orphan this one.
      invalidate();
      const controller = new AbortController();
      abortRef.current = controller;
      const myGen = genRef.current;
      const isCurrent = () => genRef.current === myGen;

      lastQueryRef.current = query;
      const aiId = tempId('a');
      const newUser = appendUser
        ? [{ id: tempId('u'), role: 'user', content: query }]
        : [];
      const aiMsg = {
        id: aiId,
        role: 'assistant',
        content: '',
        streaming: true,
      };

      setMessages(prev => [...prev, ...newUser, aiMsg]);
      patch({
        isStreaming: true,
        activeTool: null,
        errorMessage: null,
        quotaExceeded: false,
      });

      const failMessage = () =>
        setMessages(prev =>
          prev.map(m =>
            m.id === aiId
              ? { ...m, streaming: false, error: true, content: m.content }
              : m,
          ),
        );

      try {
        let conversationId = store.getState().kai?.conversationId;
        if (!conversationId) {
          const res = await service.createConversation({ businessId });
          // Superseded (new chat / business switch) while creating — discard.
          if (!isCurrent()) return;
          conversationId = res.data?.id;
          patch({ conversationId });
        }

        // Ensure branch/brand ids are loaded before /ask (race with setBusinessId).
        const scopeState = store.getState().kai;
        const needsScope =
          !scopeState?.branchIds?.length && !scopeState?.brandIds?.length;
        if (needsScope && !scopeState?.scopeLoading) {
          await loadScope(businessId);
          if (!isCurrent()) return;
        } else if (scopeState?.scopeLoading) {
          // Wait briefly for an in-flight loadScope from setBusinessId.
          const deadline = Date.now() + 8000;
          while (
            Date.now() < deadline &&
            isCurrent() &&
            store.getState().kai?.scopeLoading
          ) {
            // eslint-disable-next-line no-await-in-loop
            await new Promise(r => setTimeout(r, 50));
          }
          if (!isCurrent()) return;
        }

        const context = buildKaiContext(store.getState(), businessId);

        await service.askStream({
          conversationId,
          query,
          context,
          signal: controller.signal,
          handlers: {
            onToken: ({ delta }) => {
              if (!isCurrent() || !delta) return;
              setMessages(prev =>
                prev.map(m =>
                  m.id === aiId ? { ...m, content: m.content + delta } : m,
                ),
              );
            },
            onToolCall: ({ name, status }) => {
              if (!isCurrent()) return;
              patch({ activeTool: status === 'finished' ? null : { name } });
            },
            onUsage: u => {
              if (!isCurrent()) return;
              patch({
                usage: prev => {
                  if (!prev) return prev;
                  const remaining = u.questions_remaining;
                  const questionsUsed =
                    remaining != null && prev.questions_limit != null
                      ? Math.max(prev.questions_limit - remaining, 0)
                      : prev.questions_used;
                  return { ...prev, questions_used: questionsUsed };
                },
              });
            },
            onError: ({ message }) => {
              if (!isCurrent()) return;
              failMessage();
              patch({
                errorMessage:
                  message || 'kAI hit a problem answering. Please try again.',
              });
            },
            onDone: ({ message_id: messageId }) => {
              if (!isCurrent()) return;
              setMessages(prev =>
                prev.map(m =>
                  m.id === aiId
                    ? { ...m, streaming: false, id: messageId ?? m.id }
                    : m,
                ),
              );
            },
          },
        });
      } catch (err) {
        if (!isCurrent()) return; // stale turn — a newer turn owns the state
        if (err?.name === 'AbortError') {
          // User pressed Stop — finalise the partial answer.
          setMessages(prev =>
            prev.map(m => (m.id === aiId ? { ...m, streaming: false } : m)),
          );
        } else if (err?.status === 429) {
          // Quota exhausted before the stream started — drop the empty
          // assistant bubble and show the limit-reached state.
          setMessages(prev => prev.filter(m => m.id !== aiId));
          patch({ quotaExceeded: true });
          loadUsage(businessId);
        } else {
          const message = errorMessageOf(
            err,
            'kAI hit a problem answering. Please try again.',
          );
          failMessage();
          patch({ errorMessage: message });
        }
      } finally {
        if (isCurrent()) patch({ isStreaming: false, activeTool: null });
        // Only clear the ref if it's still ours — never clobber a newer turn's
        // controller.
        if (abortRef.current === controller) abortRef.current = null;
      }
    },
    [patch, setMessages, store, invalidate, loadUsage, loadScope],
  );

  const send = useCallback(
    query => runAsk(query, { appendUser: true }),
    [runAsk],
  );

  const retry = useCallback(() => {
    if (!lastQueryRef.current) return;
    // Drop the errored assistant bubble, keep the user turn, re-run.
    setMessages(prev => {
      const idx = [...prev].reverse().findIndex(m => m.error);
      if (idx === -1) return prev;
      const realIdx = prev.length - 1 - idx;
      return prev.filter((_, i) => i !== realIdx);
    });
    patch({ errorMessage: null });
    runAsk(lastQueryRef.current, { appendUser: false });
  }, [patch, setMessages, runAsk]);

  const cancel = useCallback(() => abortRef.current?.abort(), []);

  // Initialise the business scope when the surface is active (drawer opened or
  // full-page route mounted). Re-runs if the business list arrives after the
  // drawer is already open. Prefers the analytics selection when it's actually
  // one of the selectable businesses, else the first available.
  useEffect(() => {
    if (!active) return;
    if (kai.businessId) {
      if (!kai.usage) loadUsage(kai.businessId);
      const needsScope =
        !kai.branchIds?.length && !kai.brandIds?.length && !kai.scopeLoading;
      if (needsScope) loadScope(kai.businessId);
      return;
    }
    const naId = naSelectedBusiness?.id;
    const naSelectable = naId != null && businesses.some(b => b.id === naId);
    const defaultId = naSelectable ? naId : businesses[0]?.id ?? null;
    if (defaultId) setBusinessId(defaultId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, businesses.length, kai.businessId]);

  // Abort any in-flight stream on unmount.
  useEffect(() => () => abortRef.current?.abort(), []);

  return {
    ...kai,
    businesses,
    send,
    retry,
    cancel,
    newChat,
    openConversation,
    setBusinessId,
    loadConversations,
    loadUsage,
  };
}
